import { fullDateFullYearFormat } from "@common/helpers/date";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { useOpeningBalancesLink } from "@hooks/useOpeningBalancesLink";
import { ArrowCornerIcon } from "@icons";
import { format, parseISO, subDays } from "date-fns";
import React, { useMemo } from "react";
import { Loader } from "@design-system/Loader/Loader";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { EntityDisplay } from "@components/entity/EntityDisplay";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import styles from "./BalanceSheetImporter.module.scss";

interface Props {
    onDocumentSelect: (documentId: number) => void;
    bookkeepingStartDate: string;
    isExtracting: boolean;
}

export const UploadDocumentStep: React.FC<Props> = ({
    onDocumentSelect,
    bookkeepingStartDate,
    isExtracting,
}) => {
    const entities = useEntities({ onlyBusiness: true });
    const entitiesById = useEntitiesById({ onlyBusiness: true });
    const { selectedEntity, setFilters } = useAccountingTabContext();
    const openingBalancesLink = useOpeningBalancesLink();

    const dayBeforeStartDate = subDays(parseISO(bookkeepingStartDate), 1);
    const formattedDate = format(dayBeforeStartDate, fullDateFullYearFormat);
    const entityName = selectedEntity?.name;
    const entityItems = useMemo<Array<PickerItemData<number>>>(
        () =>
            entities.map((entity) => ({
                value: entity.id,
                label: entity.name ?? "",
                leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            })),
        [entities],
    );

    return (
        <div className={styles.uploadStep}>
            <div className={styles.uploadStepSection}>
                <h2 className={styles.uploadStepTitle}>
                    Import your Balance Sheet
                </h2>

                <div className={styles.uploadStepFieldGroup}>
                    <label className={styles.uploadStepLabel}>Entity</label>
                    <Picker.Select
                        items={entityItems}
                        value={selectedEntity?.id ?? null}
                        onValueChange={(v) => {
                            setFilters((current) => ({
                                ...current,
                                entityId: v
                                    ? entitiesById.get(v)?.id
                                    : undefined,
                            }));
                        }}
                        placeholder="Select entity"
                        renderValue={() =>
                            selectedEntity ? (
                                <EntityDisplay entity={selectedEntity} />
                            ) : undefined
                        }
                        trigger={<Picker.Button variant="primary" size="xl" />}
                    />
                </div>

                <div className={styles.uploadStepFieldGroup}>
                    <label className={styles.uploadStepLabel}>
                        Balance Sheet
                    </label>
                    <p className={styles.uploadStepDescription}>
                        Export a Balance Sheet from your previous accounting
                        system dated <strong>{formattedDate}</strong> (the day
                        before {entityName ? entityName + "'s" : "the entity's"}{" "}
                        bookkeeping start date).
                    </p>

                    {isExtracting ? (
                        <div className={styles.extractingState}>
                            <Loader inline />
                            <span>Processing...</span>
                        </div>
                    ) : (
                        <AddDocument
                            attachDocument={onDocumentSelect}
                            attachedDocumentIds={[]}
                        />
                    )}
                </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.manualSubmitSection}>
                <h2 className={styles.uploadStepTitle}>
                    Or manually submit opening balances
                </h2>
                <p className={styles.uploadStepDescription}>
                    If you don't have a Balance Sheet, we can set one up for you
                    via our form.
                </p>

                <a
                    href={openingBalancesLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.manualSubmitLink}
                >
                    <span>Submit manually</span>
                    <ArrowCornerIcon className="icon-color-gray-900 icon-size-md" />
                </a>
            </div>
        </div>
    );
};
